import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { resolveImage, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Image as ImageIcon, MessageSquare, Mail, ShoppingBag, Package, Star } from "lucide-react";
import { Stars, VerifiedBadge } from "../components/Stars";

export default function AdminDashboard() {
  const { user, logout, ready } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("gallery");
  const [gallery, setGallery] = useState([]);
  const [comments, setComments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [feedback, setFeedback] = useState([]);

  // Gallery upload form
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("T-Shirts");
  const [imageUrl, setImageUrl] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Product form
  const [pTitle, setPTitle] = useState("");
  const [pDesc, setPDesc] = useState("");
  const [pPrice, setPPrice] = useState(15.00);
  const [pSizes, setPSizes] = useState("S,M,L,XL,XXL");
  const [pImageUrl, setPImageUrl] = useState("");
  const [pFile, setPFile] = useState(null);
  const [pUploading, setPUploading] = useState(false);

  useEffect(() => {
    if (ready && !user) navigate("/admin/login");
  }, [ready, user, navigate]);

  const refresh = async () => {
    try {
      const [g, c, ct, p, o, fb] = await Promise.all([
        api.get("/gallery"),
        api.get("/comments"),
        api.get("/contact"),
        api.get("/shop/products?include_inactive=true"),
        api.get("/shop/orders"),
        api.get("/feedback"),
      ]);
      setGallery(g.data);
      setComments(c.data);
      setContacts(ct.data);
      setProducts(p.data);
      setOrders(o.data);
      setFeedback(fb.data);
    } catch (err) {
      toast.error("Failed to load data.");
    }
  };

  useEffect(() => {
    if (user) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file && !imageUrl) {
      toast.error("Provide a file or image URL.");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("title", title);
    fd.append("description", description);
    fd.append("category", category);
    if (imageUrl) fd.append("image_url", imageUrl);
    if (file) fd.append("file", file);
    try {
      await api.post("/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Gallery item added.");
      setTitle("");
      setDescription("");
      setImageUrl("");
      setFile(null);
      const fileInput = document.getElementById("admin-file");
      if (fileInput) fileInput.value = "";
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this item and all its comments?")) return;
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Deleted.");
      refresh();
    } catch {
      toast.error("Failed to delete.");
    }
  };

  const deleteComment = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/comments/${id}`);
      toast.success("Comment removed.");
      refresh();
    } catch {
      toast.error("Failed to delete comment.");
    }
  };

  const deleteFeedback = async (id) => {
    if (!window.confirm("Delete this feedback?")) return;
    try {
      await api.delete(`/feedback/${id}`);
      toast.success("Feedback removed.");
      refresh();
    } catch {
      toast.error("Failed to delete feedback.");
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/contact/${id}`);
      toast.success("Message removed.");
      refresh();
    } catch {
      toast.error("Failed to delete message.");
    }
  };

  const uploadProduct = async (e) => {
    e.preventDefault();
    if (!pFile && !pImageUrl) {
      toast.error("Provide an image file or URL.");
      return;
    }
    setPUploading(true);
    const fd = new FormData();
    fd.append("title", pTitle);
    fd.append("description", pDesc);
    fd.append("price_gbp", String(pPrice));
    fd.append("sizes", pSizes);
    if (pImageUrl) fd.append("image_url", pImageUrl);
    if (pFile) fd.append("file", pFile);
    try {
      await api.post("/shop/products", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Product added.");
      setPTitle("");
      setPDesc("");
      setPPrice(15.00);
      setPSizes("S,M,L,XL,XXL");
      setPImageUrl("");
      setPFile(null);
      const fi = document.getElementById("product-file");
      if (fi) fi.value = "";
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPUploading(false);
    }
  };

  const toggleProductActive = async (p) => {
    try {
      await api.patch(`/shop/products/${p.id}`, { active: !p.active });
      refresh();
    } catch {
      toast.error("Failed to update product.");
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/shop/products/${id}`);
      toast.success("Product removed.");
      refresh();
    } catch {
      toast.error("Failed to delete product.");
    }
  };

  if (!ready || !user) return null;

  return (
    <div data-testid="admin-dashboard" className="px-6 md:px-12 lg:px-20 py-12">
      {/* Header */}
      <div className="flex items-end justify-between border-b border-black/15 pb-6 mb-10 flex-wrap gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#71717A] mb-2">
            Studio admin
          </p>
          <h1 className="font-display font-black text-4xl md:text-5xl">
            Welcome, {user.name}
          </h1>
        </div>
        <button
          onClick={handleLogout}
          data-testid="admin-logout"
          className="btn-ink bg-black text-white inline-flex items-center gap-2 px-5 py-3 font-mono text-xs uppercase tracking-[0.25em]"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-10 border-b border-black/10 overflow-x-auto">
        {[
          { k: "gallery", label: "Gallery", icon: ImageIcon, count: gallery.length },
          { k: "products", label: "Products", icon: Package, count: products.length },
          { k: "orders", label: "Orders", icon: ShoppingBag, count: orders.length },
          { k: "comments", label: "Comments", icon: MessageSquare, count: comments.length },
          { k: "feedback", label: "Feedback", icon: Star, count: feedback.length },
          { k: "contacts", label: "Messages", icon: Mail, count: contacts.length },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`admin-tab-${t.k}`}
              className={`px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 border-b-2 -mb-px transition-colors whitespace-nowrap ${
                tab === t.k ? "border-[#E6007A] text-black" : "border-transparent text-[#71717A] hover:text-black"
              }`}
            >
              <Icon size={14} /> {t.label} <span className="text-[#71717A]">({t.count})</span>
            </button>
          );
        })}
      </div>

      {/* Gallery tab */}
      {tab === "gallery" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <form
            onSubmit={upload}
            data-testid="admin-upload-form"
            className="lg:col-span-4 border border-black/15 bg-white p-6 space-y-4 self-start"
          >
            <h2 className="font-display font-black text-2xl flex items-center gap-2">
              <Plus size={18} /> New gallery item
            </h2>
            <Input label="Title" value={title} onChange={setTitle} testid="upload-title" required />
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">
                Description
              </label>
              <textarea
                rows={3}
                value={description}
                data-testid="upload-description"
                onChange={(e) => setDescription(e.target.value)}
                className="w-full border border-black/15 px-4 py-2 font-mono text-sm focus:outline-none focus:border-black rounded-none resize-none"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">
                Category
              </label>
              <select
                value={category}
                data-testid="upload-category"
                onChange={(e) => setCategory(e.target.value)}
                className="w-full border border-black/15 px-4 py-2 font-mono text-sm focus:outline-none focus:border-black rounded-none bg-white"
              >
                {["T-Shirts", "Hoodies", "Caps", "Tote Bags", "Custom", "Behind The Scenes"].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">
                Upload image (file)
              </label>
              <input
                id="admin-file"
                type="file"
                accept="image/*"
                data-testid="upload-file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="w-full font-mono text-sm"
              />
            </div>
            <p className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
              — or —
            </p>
            <Input
              label="Image URL"
              value={imageUrl}
              onChange={setImageUrl}
              testid="upload-image-url"
            />
            <button
              type="submit"
              disabled={uploading}
              data-testid="upload-submit"
              className="btn-ink w-full bg-black text-white px-6 py-3 font-mono text-xs uppercase tracking-[0.25em] disabled:opacity-50"
            >
              {uploading ? "Uploading…" : "Add to gallery →"}
            </button>
          </form>

          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {gallery.map((g) => (
              <div
                key={g.id}
                data-testid={`admin-gallery-${g.id}`}
                className="border border-black/15 bg-white"
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#F4F4F5]">
                  <img src={resolveImage(g.image_url)} alt={g.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-4 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-display font-black text-base truncate">{g.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
                      {g.category}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteItem(g.id)}
                    data-testid={`delete-gallery-${g.id}`}
                    className="p-2 hover:bg-[#E6007A] hover:text-white transition-colors border border-black/15"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comments tab */}
      {tab === "comments" && (
        <div className="space-y-3">
          {comments.length === 0 && (
            <p className="font-mono text-sm text-[#71717A] py-12 text-center border border-dashed border-black/15">
              No comments yet.
            </p>
          )}
          {comments.map((c) => {
            const item = gallery.find((g) => g.id === c.gallery_id);
            return (
              <div
                key={c.id}
                data-testid={`admin-comment-${c.id}`}
                className="border border-black/15 bg-white p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center"
              >
                <div className="md:col-span-2">
                  <p className="font-display font-black text-base">{c.name}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
                    {new Date(c.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="md:col-span-7 font-mono text-sm leading-relaxed text-[#27272A]">
                  {c.message}
                </div>
                <div className="md:col-span-2 font-mono text-xs text-[#71717A]">
                  on <span className="text-black">{item?.title || c.gallery_id}</span>
                </div>
                <div className="md:col-span-1 flex justify-end">
                  <button
                    onClick={() => deleteComment(c.id)}
                    data-testid={`delete-comment-${c.id}`}
                    className="p-2 hover:bg-[#E6007A] hover:text-white transition-colors border border-black/15"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Feedback tab */}
      {tab === "feedback" && (
        <div className="space-y-3">
          {feedback.length === 0 && (
            <p className="font-mono text-sm text-[#71717A] py-12 text-center border border-dashed border-black/15">
              No feedback yet.
            </p>
          )}
          {feedback.map((f) => (
            <div
              key={f.id}
              data-testid={`admin-feedback-${f.id}`}
              className="border border-black/15 bg-white p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center"
            >
              <div className="md:col-span-2">
                <p className="font-display font-black text-base">{f.name}</p>
                {f.verified_buyer && <VerifiedBadge />}
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
                  {new Date(f.created_at).toLocaleString()}
                </p>
              </div>
              <div className="md:col-span-2">
                <Stars value={f.rating} size={16} testid={`admin-feedback-stars-${f.id}`} />
              </div>
              <div className="md:col-span-7 font-mono text-sm leading-relaxed text-[#27272A]">
                {f.message}
              </div>
              <div className="md:col-span-1 flex justify-end">
                <button
                  onClick={() => deleteFeedback(f.id)}
                  data-testid={`delete-feedback-${f.id}`}
                  className="p-2 hover:bg-[#E6007A] hover:text-white transition-colors border border-black/15"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Contacts tab */}
      {tab === "contacts" && (
        <div className="space-y-3">
          {contacts.length === 0 && (
            <p className="font-mono text-sm text-[#71717A] py-12 text-center border border-dashed border-black/15">
              No messages yet.
            </p>
          )}
          {contacts.map((c) => (
            <div
              key={c.id}
              data-testid={`admin-contact-${c.id}`}
              className="border border-black/15 bg-white p-5"
            >
              <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                <div>
                  <p className="font-display font-black text-lg">{c.name}</p>
                  <a
                    href={`mailto:${c.email}`}
                    className="font-mono text-xs text-[#E6007A] hover:underline"
                  >
                    {c.email}
                  </a>
                  {c.subject && (
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#71717A] mt-1">
                      Re: {c.subject}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
                    {new Date(c.created_at).toLocaleString()}
                  </p>
                  <button
                    onClick={() => deleteContact(c.id)}
                    data-testid={`delete-contact-${c.id}`}
                    className="p-2 hover:bg-[#E6007A] hover:text-white transition-colors border border-black/15"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <p className="font-mono text-sm leading-relaxed text-[#27272A] whitespace-pre-wrap">
                {c.message}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Products tab */}
      {tab === "products" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <form
            onSubmit={uploadProduct}
            data-testid="admin-product-form"
            className="lg:col-span-4 border border-black/15 bg-white p-6 space-y-4 self-start"
          >
            <h2 className="font-display font-black text-2xl flex items-center gap-2">
              <Plus size={18} /> New product
            </h2>
            <Input label="Title" value={pTitle} onChange={setPTitle} testid="product-title" required />
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">Description</label>
              <textarea rows={3} value={pDesc} data-testid="product-description" onChange={(e) => setPDesc(e.target.value)} className="w-full border border-black/15 px-4 py-2 font-mono text-sm focus:outline-none focus:border-black rounded-none resize-none" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">Price (£) *</label>
              <input type="number" step="0.01" min="0.01" required value={pPrice} data-testid="product-price" onChange={(e) => setPPrice(parseFloat(e.target.value) || 0)} className="w-full border border-black/15 px-4 py-2 font-mono text-sm focus:outline-none focus:border-black rounded-none" />
            </div>
            <Input label="Sizes (comma-separated)" value={pSizes} onChange={setPSizes} testid="product-sizes" />
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">Upload image (file)</label>
              <input id="product-file" type="file" accept="image/*" data-testid="product-file" onChange={(e) => setPFile(e.target.files?.[0] || null)} className="w-full font-mono text-sm" />
            </div>
            <p className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">— or —</p>
            <Input label="Image URL" value={pImageUrl} onChange={setPImageUrl} testid="product-image-url" />
            <button type="submit" disabled={pUploading} data-testid="product-submit" className="btn-ink w-full bg-black text-white px-6 py-3 font-mono text-xs uppercase tracking-[0.25em] disabled:opacity-50">
              {pUploading ? "Uploading…" : "Add to shop →"}
            </button>
          </form>

          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {products.length === 0 && (
              <p className="col-span-full font-mono text-sm text-[#71717A] py-12 text-center border border-dashed border-black/15">No products yet — add your first.</p>
            )}
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-${p.id}`} className={`border bg-white ${p.active ? "border-black/15" : "border-black/15 opacity-60"}`}>
                <div className="aspect-[4/3] overflow-hidden bg-[#F4F4F5]">
                  <img src={resolveImage(p.image_url)} alt={p.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="font-display font-black text-base truncate">{p.title}</p>
                    <span className="font-display font-black text-base text-[#E6007A]">£{p.price_gbp.toFixed(2)}</span>
                  </div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-3">
                    Sizes: {p.sizes.join(" · ")} · {p.active ? "ACTIVE" : "HIDDEN"}
                  </p>
                  <div className="flex gap-2">
                    <button onClick={() => toggleProductActive(p)} data-testid={`toggle-product-${p.id}`} className="flex-1 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] border border-black/15 hover:bg-black hover:text-white">
                      {p.active ? "Hide" : "Publish"}
                    </button>
                    <button onClick={() => deleteProduct(p.id)} data-testid={`delete-product-${p.id}`} className="p-2 hover:bg-[#E6007A] hover:text-white border border-black/15">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Orders tab */}
      {tab === "orders" && (
        <div className="space-y-3">
          {orders.length === 0 && (
            <p className="font-mono text-sm text-[#71717A] py-12 text-center border border-dashed border-black/15">No orders yet.</p>
          )}
          {orders.map((o) => {
            const addr = o.shipping_address || {};
            const isPaid = o.payment_status === "paid";
            return (
              <div key={o.session_id} data-testid={`admin-order-${o.session_id}`} className="border border-black/15 bg-white p-5">
                <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <p className="font-display font-black text-lg">{addr.name || "—"}</p>
                      <span
                        className={`font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-1 ${
                          isPaid
                            ? "bg-[#FFD600] text-black"
                            : o.payment_status === "initiated"
                            ? "bg-[#F4F4F5] text-[#71717A]"
                            : "bg-[#E6007A] text-white"
                        }`}
                      >
                        {o.payment_status}
                      </span>
                    </div>
                    <a href={`mailto:${addr.email || ""}`} className="font-mono text-xs text-[#E6007A] hover:underline">{addr.email}</a>
                    {addr.phone && <span className="font-mono text-xs text-[#71717A]"> · {addr.phone}</span>}
                  </div>
                  <div className="text-right">
                    <p className="font-display font-black text-2xl">£{(o.amount_gbp || 0).toFixed(2)}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{new Date(o.created_at).toLocaleString()}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-black/10 pt-4">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Items</p>
                    <ul className="font-mono text-sm space-y-1">
                      {(o.items || []).map((it, idx) => (
                        <li key={idx}>{it.title} ({it.size}) × {it.quantity} — £{(it.line_total_gbp || 0).toFixed(2)}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Ship to</p>
                    <p className="font-mono text-sm leading-relaxed text-[#27272A]">
                      {addr.address_line1}<br />
                      {addr.address_line2 && <>{addr.address_line2}<br /></>}
                      {addr.city} {addr.postcode}<br />
                      {addr.country}
                    </p>
                  </div>
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mt-4 pt-3 border-t border-black/10">
                  Order ref: {o.session_id?.slice(-16)}
                  {o.review_invite_sent && (
                    <span data-testid={`invite-sent-${o.session_id}`} className="ml-3 text-[#0E7490]">
                      ✓ Review invite sent
                    </span>
                  )}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">
        {label} {required && "*"}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-black/15 px-4 py-2 font-mono text-sm focus:outline-none focus:border-black rounded-none"
      />
    </div>
  );
}
